// LUMINA · pages/EnergyDashboard.jsx
// Phase 58: 澄光日课 — main dashboard page at /dashboard

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DailyEnergyWidget from '../components/DailyEnergyWidget';
import EnergyForecastPanel from '../components/EnergyForecastPanel';
import WeeklyEnergyCalendar from '../components/WeeklyEnergyCalendar';
import EnergyTierGate from '../components/EnergyTierGate';
import { subscribePush, unsubscribePush, getCurrentSubscription, isPushSupported, getPushPermission } from '../utils/pushNotifications';
import { API } from '../constants/config';

const GD = "#D4AF37";
const BG = "#FDFBF7";
const TXT = "rgba(0,0,0,0.82)";
const TXT2 = "rgba(0,0,0,0.45)";

export default function EnergyDashboard({ user, setPage, lang = "zh" }) {
  const navigate = useNavigate();
  const zh = lang === "zh";
  const token = localStorage.getItem("lumina_token") || "";

  // Get birth data from localStorage (populated after audit)
  const [birthData, setBirthData] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  // Settings state
  const [prefs, setPrefs] = useState({ daily_energy_email: false, daily_energy_push: false });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [pushStatus, setPushStatus] = useState("unknown"); // unknown|granted|denied|subscribed
  const [pushLoading, setPushLoading] = useState(false);
  // Qimen daily data for arbiter layer (Phase 89)
  const [qimenData, setQimenData] = useState(null);
  // Phase 102: Critical Dates + Reports
  const [criticalDates, setCriticalDates] = useState(null);
  const [reports, setReports] = useState(null);

  // Derived state (must be before hooks that reference them)
  const isLoggedIn = !!user && !!token;
  const hasBirthData = !!birthData;

  useEffect(() => {
    // Try to load birth data from last audit
    try {
      const stored = localStorage.getItem("lumina_last_birth");
      if (stored) setBirthData(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    const tier = user?.tier;
    setHasAccess(tier === "pro");
  }, [user]);

  // Load preferences
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/daily-energy-prefs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPrefs(d); })
      .catch(() => {});
  }, [token]);

  // Fetch qimen daily data for arbiter layer
  useEffect(() => {
    if (!hasBirthData) return;
    fetch(`${API}/qimen/daily`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.available) setQimenData(d); })
      .catch(() => {});
  }, [hasBirthData]);

  // Phase 102: Fetch critical dates
  useEffect(() => {
    if (!hasBirthData || !token) return;
    fetch(`${API}/audit/critical-dates?year=${new Date().getFullYear()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCriticalDates(d); })
      .catch(() => {});
  }, [hasBirthData, token]);

  // Fetch user's audit reports
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/reports`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setReports(d); })
      .catch(() => {});
  }, [token]);

  // Check push subscription status
  useEffect(() => {
    isPushSupported().then(supported => {
      if (!supported) { setPushStatus("unsupported"); return; }
      getPushPermission().then(perm => {
        if (perm === "denied") { setPushStatus("denied"); return; }
        getCurrentSubscription().then(sub => {
          setPushStatus(sub ? "subscribed" : perm === "granted" ? "granted" : "default");
        });
      });
    });
  }, []);

  const toggleEmailPref = async (val) => {
    setPrefsLoading(true);
    await fetch(`${API}/auth/daily-energy-prefs`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ daily_energy_email: val }),
    }).catch(() => {});
    setPrefs(p => ({ ...p, daily_energy_email: val }));
    setPrefsLoading(false);
  };

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushStatus === "subscribed") {
        await unsubscribePush(token);
        await fetch(`${API}/auth/daily-energy-prefs`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ daily_energy_push: false }),
        });
        setPrefs(p => ({ ...p, daily_energy_push: false }));
        setPushStatus("default");
      } else {
        await subscribePush(token);
        await fetch(`${API}/auth/daily-energy-prefs`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ daily_energy_push: true }),
        });
        setPrefs(p => ({ ...p, daily_energy_push: true }));
        setPushStatus("subscribed");
      }
    } catch (e) {
      if (e.message === "Push permission denied") setPushStatus("denied");
    }
    setPushLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Noto Serif SC','Noto Serif',serif" }}>

      {/* ── Header ── */}
      <div style={{
        padding: "24px 32px 0",
        borderBottom: "1px solid rgba(212,175,55,0.15)",
        background: "rgba(253,251,247,0.97)",
      }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 20 }}>
            <div>
              <div style={{ fontSize: 10, color: GD, letterSpacing: 4, marginBottom: 4, textTransform: "uppercase" }}>
                LUMINAARC · 澄光日课
              </div>
              <div style={{ fontSize: 24, color: TXT, letterSpacing: 2, fontWeight: 500 }}>
                每日能量日历
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 80px" }}>

        {/* ── 命理报告 (quick access) ── */}
        {isLoggedIn && reports !== null && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: GD, letterSpacing: 3, textTransform: "uppercase" }}>
                📋 能量报告
              </div>
              <span onClick={() => navigate("/audit")} style={{
                fontSize: 12, color: GD, cursor: "pointer", letterSpacing: .5, textDecoration: "underline"
              }}>
                {reports.length > 0 ? "生成新报告 →" : "生成第一份报告 →"}
              </span>
            </div>
            {reports.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 10 }}>
                {reports.slice(0, 3).map((r, i) => (
                  <div key={i} onClick={() => navigate("/audit")} style={{
                    background: "#fff", border: "1px solid rgba(212,175,55,0.15)",
                    borderRadius: 10, padding: "14px 16px", cursor: "pointer",
                  }}>
                    <div style={{ fontSize: 10, color: TXT2, marginBottom: 4 }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("zh-CN") : ""}
                    </div>
                    <div style={{ fontSize: 13, color: TXT, fontWeight: 500, letterSpacing: .3, marginBottom: 3 }}>
                      {r.concern || r.tier_label || "八字能量报告"}
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{
                        fontSize: 11, color: "#fff", background: GD,
                        padding: "2px 8px", borderRadius: 10, letterSpacing: .5,
                      }}>
                        {r.score != null ? `${r.score}分` : r.tier_label}
                      </span>
                      <span style={{ fontSize: 10, color: TXT2 }}>{r.birth || ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: "center", padding: "20px 0",
                background: "rgba(212,175,55,0.03)", border: "1px dashed rgba(212,175,55,0.2)",
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, color: TXT2, letterSpacing: .5 }}>
                  尚未生成能量报告。完成一次八字排盘后，报告将在此展示。
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Not logged in ── */}
        {!isLoggedIn && (
          <div style={{
            textAlign: "center", padding: "64px 0",
            background: "rgba(212,175,55,0.03)",
            border: "1px solid rgba(212,175,55,0.15)",
            borderRadius: 16,
            marginTop: 24,
          }}>
            <div style={{
              fontSize: 40, marginBottom: 16,
            }}>🔐</div>
            <div style={{
              fontSize: 17, color: TXT, fontWeight: 600,
              marginBottom: 8, letterSpacing: .5,
            }}>
              请登录以查看您的仪表盘
            </div>
            <div style={{
              fontSize: 13, color: TXT2, marginBottom: 28,
              letterSpacing: .5, lineHeight: 1.7, maxWidth: 360,
              margin: "0 auto 28px",
            }}>
              Please sign in to access your dashboard and daily energy calendar.
              <br/>
              {zh ? "登录后生成八字报告即可开启澄光日课。" : "Generate your first Bazi report after signing in to unlock daily energy insights."}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => navigate("/login")} style={{
                padding: "12px 36px",
                background: `linear-gradient(135deg,${GD},#A07828)`,
                border: "none", borderRadius: 10, color: "#fff",
                fontSize: 14, fontWeight: 600, cursor: "pointer", letterSpacing: 1,
              }}>
                {zh ? "登录 →" : "Sign In →"}
              </button>
              <button onClick={() => navigate("/register")} style={{
                padding: "12px 36px",
                background: "transparent",
                border: `1px solid ${GD}`,
                borderRadius: 10, color: GD,
                fontSize: 14, fontWeight: 500, cursor: "pointer", letterSpacing: 1,
              }}>
                {zh ? "免费注册" : "Register Free"}
              </button>
            </div>
          </div>
        )}

        {/* ── Logged in but no birth data ── */}
        {isLoggedIn && !hasBirthData && (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <div style={{ fontSize: 14, color: TXT2, marginBottom: 24, letterSpacing: .5, lineHeight: 1.8 }}>
              请先完成一次八字报告，澄光日课将基于您的命盘推演每日能量
            </div>
            <button onClick={() => navigate("/audit")} style={{
              padding: "12px 40px",
              background: `linear-gradient(135deg,${GD},#A07828)`,
              border: "none", borderRadius: 10, color: "#fff",
              fontSize: 14, cursor: "pointer", letterSpacing: 1.5,
            }}>
              生成我的八字报告 →
            </button>
          </div>
        )}

        {/* ── Main content ── */}
        {isLoggedIn && hasBirthData && (
          <>
          <style>{`
            .energy-dashboard-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 24px;
            }
            @media (max-width: 768px) {
              .energy-dashboard-grid {
                grid-template-columns: 1fr;
                gap: 16px;
              }
            }
          `}</style>
          <div className="energy-dashboard-grid">

            {/* Daily widget */}
            <div>
              <DailyEnergyWidget
                birthData={birthData}
                token={token}
                compact={false}
                onNavigateDashboard={null}
                qimenData={qimenData}
              />

              {/* Trial/upgrade banner */}
              {trialDaysLeft !== null && trialDaysLeft > 0 && (
                <div style={{
                  marginTop: 12,
                  background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.2)",
                  borderRadius: 10, padding: "10px 16px", textAlign: "center",
                  fontSize: 12, color: TXT2, letterSpacing: .5,
                }}>
                  澄光日课 7 天体验 · 剩余 {trialDaysLeft} 天 ·{" "}
                  <span onClick={() => navigate("/pricing")} style={{ color: GD, cursor: "pointer", textDecoration: "underline" }}>
                    升级订阅 →
                  </span>
                </div>
              )}
            </div>

            {/* Energy Forecast Panel — 7天/1个月/1季度 */}
            <div>
              {hasAccess ? (
                <EnergyForecastPanel birthData={birthData} token={token}/>
              ) : (
                <div>
                  <div style={{ fontSize: 11, color: GD, letterSpacing: 3, marginBottom: 12,
                    textTransform: "uppercase" }}>🔮 能量前瞻 · 7天/月/季</div>
                  <EnergyTierGate daysLeft={null} onUpgrade={() => navigate("/pricing")}/>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {/* ── 今日奇门行动指南 (Phase 67) ── */}
        <QimenDailyCard />

        {/* ── 节点预警 (Phase 102) ── */}
        {isLoggedIn && hasBirthData && criticalDates && (
          <div style={{ marginTop: 28, paddingTop: 28, borderTop: "1px solid rgba(212,175,55,0.1)" }}>
            <div style={{ fontSize: 11, color: GD, letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>
              🔮 节点预警
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 14 }}>
              {/* Dayun countdown */}
              {criticalDates.dayun_countdown && (
                <div style={{
                  background: "rgba(212,175,55,0.04)", border: "1px solid rgba(212,175,55,0.15)",
                  borderRadius: 12, padding: "16px 18px",
                }}>
                  <div style={{ fontSize: 10, color: TXT2, letterSpacing: 1, marginBottom: 4 }}>大运切换</div>
                  <div style={{ fontSize: 15, color: TXT, fontWeight: 600, letterSpacing: .5 }}>
                    {criticalDates.dayun_countdown.current_dayun}
                  </div>
                  <div style={{ fontSize: 12, color: GD, marginTop: 6 }}>
                    → {criticalDates.dayun_countdown.next_dayun}
                  </div>
                  <div style={{ fontSize: 11, color: TXT2, marginTop: 4 }}>
                    剩余 {criticalDates.dayun_countdown.days_remaining} 天 · {criticalDates.dayun_countdown.transition_date}
                  </div>
                </div>
              )}

              {/* This month clash days */}
              <div style={{
                background: "#fff", border: "1px solid rgba(212,175,55,0.15)",
                borderRadius: 12, padding: "16px 18px",
              }}>
                <div style={{ fontSize: 10, color: TXT2, letterSpacing: 1, marginBottom: 4 }}>本月冲日</div>
                {(() => {
                  const now = new Date();
                  const thisMonth = criticalDates.clash_days
                    .filter(cd => {
                      const d = new Date(cd.date);
                      return d.getMonth() === now.getMonth() && cd.severity === "high";
                    })
                    .slice(0, 5);
                  return thisMonth.length > 0 ? (
                    thisMonth.map((cd, i) => (
                      <div key={i} style={{ fontSize: 12, color: TXT, marginBottom: 3, letterSpacing: .3 }}>
                        <span style={{ color: "#c53030", fontWeight: 600 }}>{cd.date.slice(5)}</span>
                        {" "}{cd.description.slice(0, 30)}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 13, color: "#38a169", fontWeight: 500 }}>本月无高危冲日</div>
                  );
                })()}
              </div>

              {/* Next 节气 */}
              <div style={{
                background: "#fff", border: "1px solid rgba(212,175,55,0.15)",
                borderRadius: 12, padding: "16px 18px",
              }}>
                <div style={{ fontSize: 10, color: TXT2, letterSpacing: 1, marginBottom: 4 }}>节气节点</div>
                {(() => {
                  const now = new Date();
                  const upcoming = criticalDates.month_transitions
                    .filter(t => t.month_ganzhi && new Date(t.date) >= now)
                    .slice(0, 3);
                  return upcoming.map((t, i) => (
                    <div key={i} style={{ fontSize: 12, color: TXT, marginBottom: 4, letterSpacing: .3 }}>
                      <span style={{ color: GD, fontWeight: 600 }}>{t.date.slice(5)}</span>
                      {" "}{t.name} → {t.month_ganzhi}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}

        {/* ── 推送设置 ── */}
        {isLoggedIn && (
          <div style={{ marginTop: 32, paddingTop: 28, borderTop: "1px solid rgba(212,175,55,0.1)" }}>
            <div style={{ fontSize: 11, color: GD, letterSpacing: 3, textTransform: "uppercase", marginBottom: 16 }}>
              推送设置
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Email toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", border: "1px solid rgba(212,175,55,0.2)",
                borderRadius: 10, padding: "14px 18px" }}>
                <div>
                  <div style={{ fontSize: 13, color: TXT, fontWeight: 500, letterSpacing: .5 }}>每日晨报邮件</div>
                  <div style={{ fontSize: 11, color: TXT2, marginTop: 3, letterSpacing: .5 }}>
                    每天早上 7:00 收到今日能量简报
                  </div>
                </div>
                <button onClick={() => toggleEmailPref(!prefs.daily_energy_email)}
                  disabled={prefsLoading}
                  style={{
                    width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                    background: prefs.daily_energy_email ? GD : "rgba(0,0,0,0.15)",
                    position: "relative", transition: "background .2s",
                    opacity: prefsLoading ? 0.6 : 1,
                  }}>
                  <div style={{
                    position: "absolute", top: 3,
                    left: prefs.daily_energy_email ? 23 : 3,
                    width: 18, height: 18, borderRadius: "50%",
                    background: "#fff", transition: "left .2s",
                  }}/>
                </button>
              </div>

              {/* Browser push toggle */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", border: "1px solid rgba(212,175,55,0.2)",
                borderRadius: 10, padding: "14px 18px" }}>
                <div>
                  <div style={{ fontSize: 13, color: TXT, fontWeight: 500, letterSpacing: .5 }}>浏览器推送通知</div>
                  <div style={{ fontSize: 11, color: TXT2, marginTop: 3, letterSpacing: .5 }}>
                    {pushStatus === "denied" ? "已拒绝通知权限，请在浏览器设置中开启"
                      : pushStatus === "unsupported" ? "此浏览器不支持推送通知"
                      : "每天收到今日能量推送（需授权通知）"}
                  </div>
                </div>
                <button
                  onClick={handlePushToggle}
                  disabled={pushLoading || pushStatus === "denied" || pushStatus === "unsupported"}
                  style={{
                    width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                    background: pushStatus === "subscribed" ? GD : "rgba(0,0,0,0.15)",
                    position: "relative", transition: "background .2s",
                    opacity: (pushLoading || pushStatus === "denied" || pushStatus === "unsupported") ? 0.5 : 1,
                  }}>
                  <div style={{
                    position: "absolute", top: 3,
                    left: pushStatus === "subscribed" ? 23 : 3,
                    width: 18, height: 18, borderRadius: "50%",
                    background: "#fff", transition: "left .2s",
                  }}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── How it works ── */}
        <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid rgba(212,175,55,0.1)" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 11, color: GD, letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>
              日课原理
            </div>
            <div style={{ fontSize: 13, color: TXT2, lineHeight: 1.8, maxWidth: 560, margin: "0 auto" }}>
              澄光日课基于您的八字原局，每日推演流日天干与您的喜用忌神的相互关系，
              结合刑冲合害与十神叙事，给出今日能量走向与修行建议。
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, maxWidth: 600, margin: "0 auto" }}>
            {[
              { icon: "⚖️", title: "喜忌系数", desc: "流日五行与喜用忌神的相克生扶关系（50%权重）" },
              { icon: "🌀", title: "刑冲合害", desc: "流日地支与四柱地支的刑冲合害互动（30%权重）" },
              { icon: "✦", title: "十神叙事", desc: "流日天干对日主产生的十神类型与叙事方向（20%权重）" },
            ].map(item => (
              <div key={item.title} style={{
                background: "#fff", border: "1px solid rgba(212,175,55,0.2)",
                borderRadius: 12, padding: "18px 16px", textAlign: "center",
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icon}</div>
                <div style={{ fontSize: 12, color: GD, letterSpacing: 1, marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: TXT2, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// Phase 67: 今日奇门行动指南卡片
function QimenDailyCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/qimen/daily`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.available) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  return (
    <div style={{
      marginTop: 28, padding: "18px 20px 22px",
      background: "rgba(6,78,59,0.03)", border: "1px solid rgba(6,78,59,0.12)",
      borderRadius: 12,
    }}>
      <div style={{ fontSize: 11, color: "#064E3B", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>
        📿 今日奇门行动指南 · {data.yin_yang_dun}第{data.ju_shu}局
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        {data.favorable_direction && (
          <div style={{ padding: "10px 14px", background: "rgba(6,78,59,0.06)", borderRadius: 8, fontSize: 13 }}>
            <span style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 2 }}>🐴 马星吉方</span>
            <span style={{ color: "#064E3B", fontWeight: 600 }}>{data.favorable_direction}</span>
          </div>
        )}
        {data.avoid_direction && (
          <div style={{ padding: "10px 14px", background: "rgba(156,163,175,0.08)", borderRadius: 8, fontSize: 13 }}>
            <span style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 2 }}>⚡ 空亡暂缓</span>
            <span style={{ color: "#6b7280", fontWeight: 600 }}>{data.avoid_direction} 方</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, marginBottom: 10 }}>
        {data.narrative || data.action_advice}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 12, color: "#064E3B", fontWeight: 600 }}>{data.summary_line}</div>
        <a href="/qimen" style={{ fontSize: 11, color: "#064E3B", textDecoration: "none", opacity: 0.7 }}>
          完整排盘 →
        </a>
      </div>
    </div>
  );
}
